import { flow, getEnv, Instance, types } from "mobx-state-tree";
import { ProjectPropertyCreateInput } from "../../api-models/request/ProjectPropertyInput";
import {
  CustomProjectPropertyOptionResponse,
  CustomProjectPropertyValueResponse,
} from "../../api-models/response/CustomProjectPropertyValueResponse";
import { endpoints } from "../api/ApiEndpoints";
import { StorageModel } from "../storage/StorageModel";

export interface CustomProperty {
  id: number;
  label: string;
  type: number;
  columnId: number;
  index: number;
  value: any;
  options?: CustomProjectPropertyOptionResponse[];
}

export interface Column {
  id: number;
  name: string;
  properties: CustomProperty[];
}

export const emptyCustomProperty: CustomProperty = {
  id: 0,
  label: "",
  type: 0,
  columnId: 0,
  index: 0,
  value: "",
};

const TotalNumberOfColumns = 4;

const ProjectPropertiesModel = types.model({
  itemsCount: 0,
});

export const CustomPropertiesModel = types
  .model()
  .named("CustomPropertiesModel")
  .actions((self) => {
    const createColumns = () => {
      let columns: Column[] = [];
      for (let index = 0; index < TotalNumberOfColumns; index++) {
        const column: Column = {
          id: index,
          name: `${index + 1}`,
          properties: [],
        };
        columns.push(column);
      }
      return columns;
    };

    const addPropertiesToColumns = (properties: CustomProperty[], columns: Column[]) => {
      properties.forEach((property) => {
        columns[property.columnId].properties.push(property);
      });
    };

    const sortPropertiesInColumns = (columns: Column[]) => {
      columns.forEach((column) => {
        column.properties.sort(
          (property1: CustomProperty, property2: CustomProperty) =>
            property1.index - property2.index
        );
      });
    };

    return {
      getMappedColumns(response: CustomProjectPropertyValueResponse[]) {
        let properties = response as unknown as CustomProperty[];
        let columns = createColumns();

        addPropertiesToColumns(properties, columns);

        sortPropertiesInColumns(columns);
        return columns;
      },
    };
  });

export type CustomPropertiesStorage = Instance<typeof CustomPropertiesModel>;

export const ProjectPropertiesStorageModel = types
  .compose(ProjectPropertiesModel, StorageModel, CustomPropertiesModel)
  .named("ProjectPropertyStorage")
  .actions((self) => {
    const getCustomProjectProperties = (columns: Column[]) => {
      let properties: CustomProperty[] = [];

      columns.forEach((column) => {
        properties.push(...column.properties);
      });
      return properties;
    };
    return {
      createProjectProperty: flow(function* createProjectProperty(
        projectProperty: ProjectPropertyCreateInput
      ) {
        return yield getEnv(self).api.post(self.endpoint, projectProperty);
      }),
      updatePosition: flow(function* updatePosition() {
        let columns = self.data.columns as Column[];
        let properties: CustomProperty[] = [];

        columns.forEach((column) => {
          properties.push(...column.properties);
        });

        return yield getEnv(self).api.put(
          endpoints.get("custom_project_property_positions"),
          properties
        );
      }),
      mapResponse(response: any) {
        let properties = response as CustomProperty[];
        self.itemsCount = properties.length;

        let columns = self.getMappedColumns(response);

        self.data = {
          columns: columns,
        };
      },
      mapCustomProjectProperties() {
        let columns = self.data.columns as Column[];
        var mappedProperties = getCustomProjectProperties(columns);
        return mappedProperties;
      },
    };
  })
  .views((self) => {
    return {
      get endpoint() {
        return endpoints.get("custom_project_property");
      },
      get initialValues() {
        const extractedFields = {};
        if (!self.isLoading) {
          var properties = self.mapCustomProjectProperties();
          properties.forEach((property: any) => {
            const label = property.label;
            const value = property.value || "";
            extractedFields[label] = value;
          });
        }
        return extractedFields;
      },
    };
  });

export type ProjectPropertyStorage = Instance<typeof ProjectPropertiesStorageModel>;
